import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useLocalStorage } from "@rehooks/local-storage";
import { useMemo } from "react";

import usePaginationCursorReset from "components/Table/Pagination/usePaginationCursorReset";
import {
  INTENT_PROJECTS_LIMIT,
  INTENT_PROJECTS_LIMIT_STORAGE_KEY,
} from "views/Account/IntentProjects/Table/constants";

import useIntentProjectsFiltersVariables from "../useSearchIntentProjectsSuggestions/useFiltersVariables";
import { SEARCH_INTENT_PROJECTS } from "./gql";
import useIntentProjectsStore from "./store";

const useSearchIntentProjects = () => {
  const {
    cursor: storedCursor,
    requestedPage: storedRequestedPage,
    resetPagination,
  } = useIntentProjectsStore();
  const [storedRowsPerPage] = useLocalStorage<number>(INTENT_PROJECTS_LIMIT_STORAGE_KEY);
  const { fullTextSearch, predicates, filterVariablesLoaded, sortOptionFields } =
    useIntentProjectsFiltersVariables();

  const first = storedRowsPerPage || INTENT_PROJECTS_LIMIT;

  const paginationResetKey = useMemo(
    () => ({ fullTextSearch, predicates, sortOptionFields }),
    [fullTextSearch, predicates, sortOptionFields]
  );

  const { cursor, requestedPage } = usePaginationCursorReset({
    resetKey: paginationResetKey,
    cursor: storedCursor,
    requestedPage: storedRequestedPage,
    resetPagination,
  });

  const { data, error, networkStatus, previousData } = useQuery(SEARCH_INTENT_PROJECTS, {
    variables: {
      input: {
        fullTextSearch,
        first,
        after: cursor,
        orderBy: sortOptionFields,
        predicates,
        requestedPage,
      },
    },
    skip: !filterVariablesLoaded,
    notifyOnNetworkStatusChange: true,
  });

  const intentProjects = useMemo(() => {
    const edges =
      data?.searchIntentProjects.edges ?? previousData?.searchIntentProjects.edges ?? [];

    return edges.map((edge) => edge.node);
  }, [data, previousData]);

  const initialLoading =
    (intentProjects.length === 0 && networkStatus === NetworkStatus.loading) ||
    !filterVariablesLoaded;

  const loadingContent =
    !filterVariablesLoaded ||
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.fetchMore ||
    networkStatus === NetworkStatus.setVariables;

  const hasLoadedPage = Boolean(data);
  const isEmpty =
    hasLoadedPage &&
    !intentProjects.length &&
    !initialLoading &&
    !fullTextSearch &&
    !predicates.length;
  const searchIsEmpty =
    hasLoadedPage && !intentProjects.length && !!(fullTextSearch || predicates.length > 0);

  return {
    intentProjects,
    error,
    initialLoading,
    loadingContent,
    isEmpty,
    searchIsEmpty,
    startCursor: data?.searchIntentProjects?.pageInfo.startCursor,
    endCursor: data?.searchIntentProjects?.pageInfo.endCursor,
    paginationResetKey,
  };
};

export default useSearchIntentProjects;
